import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import { getConfig } from '@/configs'
import { toCamelCase, toSnakeCase } from '@/utils/caseConverter'

const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 10 * 1000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  instance.interceptors.request.use(
    async config => {
      if (config.data) {
        config.data = toSnakeCase(config.data)
      }
      if (config.params) {
        config.params = toSnakeCase(config.params)
      }

      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.data) {
        response.data = toCamelCase(response.data)
      }

      return response
    },
    error => {
      return Promise.reject(error)
    }
  )

  return instance
}

// Axios 인스턴스 생성을 비동기로 지연시킴 (Top-level await 방지)
const configPromise = getConfig()

const offerwallListInstancePromise = configPromise.then(config => createAxiosInstance(config.adisonOfwList.baseUrl))
const offerwallInstancePromise = configPromise.then(config => createAxiosInstance(config.adisonOfw.baseUrl))
const offerwallGlobalInstancePromise = configPromise.then(config => createAxiosInstance(config.adisonOfwGlobal.baseUrl))

//api 메소드 생성 함수
const createApiMethods = (instance: AxiosInstance) => ({
  get: <Req = unknown, Res = unknown>(url: string, config?: AxiosRequestConfig<Req>) => instance.get<Res>(url, config),

  post: <Req = unknown, Res = unknown>(url: string, data?: Req, config?: AxiosRequestConfig<Req>) =>
    instance.post<Res>(url, data, config),

  put: <Req = unknown, Res = unknown>(url: string, data?: Req, config?: AxiosRequestConfig<Req>) =>
    instance.put<Res>(url, data, config),

  delete: <Req = unknown, Res = unknown>(url: string, config?: AxiosRequestConfig<Req>) =>
    instance.delete<Res>(url, config)
})

type ApiMethods = ReturnType<typeof createApiMethods>

const createDeferredApiMethods = (instancePromise: Promise<AxiosInstance>): ApiMethods => {
  // 인스턴스가 준비되면 API 메서드를 반환
  // 각 메서드 호출 시 인스턴스가 준비될 때까지 기다린 후 실제 호출 수행
  return {
    get: <Req = unknown, Res = unknown>(url: string, config?: AxiosRequestConfig<Req>) =>
      instancePromise.then(instance => instance.get<Res>(url, config)),
    post: <Req = unknown, Res = unknown>(url: string, data?: Req, config?: AxiosRequestConfig<Req>) =>
      instancePromise.then(instance => instance.post<Res>(url, data, config)),
    put: <Req = unknown, Res = unknown>(url: string, data?: Req, config?: AxiosRequestConfig<Req>) =>
      instancePromise.then(instance => instance.put<Res>(url, data, config)),
    delete: <Req = unknown, Res = unknown>(url: string, config?: AxiosRequestConfig<Req>) =>
      instancePromise.then(instance => instance.delete<Res>(url, config))
  } as ApiMethods
}

export const offerwallListApi = createDeferredApiMethods(offerwallListInstancePromise)
export const offerwallApi = createDeferredApiMethods(offerwallInstancePromise)
export const offerwallGlobalApi = createDeferredApiMethods(offerwallGlobalInstancePromise)
